import { useEffect, useRef, useState } from 'react'

import { useApiClient } from '@/api/ApiClientProvider'
import type { CustomerReveal } from '@/api/customers'

/*
 * The reveal: one of the two requests that identify a person, so never cached. Each
 * revealed id is held in the page's own state only, never in a URL, a query key, the
 * query cache, the browser's storage or a log, and a reveal is never retried on its own,
 * because every reveal is a new record in the audit log.
 */

export type RevealState =
  | { status: 'loading' }
  | { status: 'revealed'; reveal: CustomerReveal }
  | { status: 'failed'; error: unknown }

/**
 * The ids revealed on this page, by `customer_id`, kept only in the page's state: gone
 * when the page is left, and hidden again on request. A reveal that settles after the
 * page has gone is dropped.
 */
export function useReveals() {
  const client = useApiClient()
  const [reveals, setReveals] = useState<ReadonlyMap<string, RevealState>>(new Map())
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const set = (customerId: string, next: RevealState | null) =>
    setReveals((current) => {
      const copy = new Map(current)
      if (next) copy.set(customerId, next)
      else copy.delete(customerId)
      return copy
    })

  return {
    reveals,
    reveal: (customerId: string) => {
      set(customerId, { status: 'loading' })
      client.revealCustomer(customerId).then(
        (reveal) => {
          if (mounted.current) set(customerId, { status: 'revealed', reveal })
        },
        (error: unknown) => {
          if (mounted.current) set(customerId, { status: 'failed', error })
        },
      )
    },
    hide: (customerId: string) => set(customerId, null),
  }
}
